import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy, limit } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Client Dashboard — chat transcripts, feature & content requests (client profile card)
//
// What clients asked the dashboard chat (every message is stored, with the
// months/data each answer used), the feature requests they sent (name +
// message; Received → Under review → Planned → Done), and content requests
// (Submitted → In progress → In review → Delivered, each also a "Content
// Request" task on the task board). Clients see the statuses set here.
// ---------------------------------------------------------------------------

const STATUSES = [['submitted', 'Submitted'], ['in_progress', 'In progress'], ['in_review', 'In review'], ['delivered', 'Delivered']];
const FEATURE_STATUSES = [['received', 'Received'], ['reviewing', 'Under review'], ['planned', 'Planned'], ['done', 'Done']];
const fmt = ts => (ts?.toDate ? ts.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export default function DashboardChatPanel({ clientId, client }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('features');
  const [requests, setRequests] = useState([]);
  const [features, setFeatures] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const u1 = onSnapshot(collection(db, 'clients', clientId, 'contentRequests'), s => {
      setRequests(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    }, () => setRequests([]));
    const u2 = onSnapshot(query(collection(db, 'clients', clientId, 'dashboardChat'), orderBy('at', 'desc'), limit(80)), s => {
      setMessages(s.docs.map(d => ({ id: d.id, ...d.data() })).reverse());
    }, () => setMessages([]));
    const u3 = onSnapshot(collection(db, 'clients', clientId, 'featureRequests'), s => {
      setFeatures(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    }, () => setFeatures([]));
    return () => { u1(); u2(); u3(); };
  }, [open, clientId]);

  const setStatus = async (r, status) => {
    try {
      await updateDoc(doc(db, 'clients', clientId, 'contentRequests', r.id), { status, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || null });
      if (status === 'delivered' && r.taskId) await updateDoc(doc(db, 'tasks', r.taskId), { completed: true }).catch(() => {});
      setMsg({ ok: true, text: 'Status updated. The client sees it in their request list.' });
    } catch (err) { setMsg({ ok: false, text: err.message }); }
  };

  const setFeatureStatus = async (f, status) => {
    try {
      await updateDoc(doc(db, 'clients', clientId, 'featureRequests', f.id), { status, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.email || null });
      setMsg({ ok: true, text: 'Status updated. The client sees it in their request list.' });
    } catch (err) { setMsg({ ok: false, text: err.message }); }
  };

  const openCount = requests.filter(r => r.status !== 'delivered').length;
  const openFeatures = features.filter(f => f.status !== 'done').length;
  const cap = client?.dashboardSources?.chatMonthlyCap || 150;
  const thisMonth = `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, '0')}`;
  const usedThisMonth = messages.filter(m => m.role === 'user' && m.monthKey === thisMonth).length;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Dashboard Chat &amp; Requests</div>
          {open && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{openFeatures} open feature request{openFeatures === 1 ? '' : 's'} · {openCount} open content request{openCount === 1 ? '' : 's'} · {usedThisMonth}/{cap} questions this month</span>}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {[['features', 'Feature requests'], ['requests', 'Content requests'], ['chat', 'Chat transcripts']].map(([k, l]) => (
              <button key={k} type="button" className={`btn ${tab === k ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 12 }} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
          {msg && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</div>}

          {tab === 'features' && (
            <>
              {features.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No feature requests yet.</div>}
              {features.map(f => (
                <div key={f.id} style={{ borderTop: '1px solid var(--border-color, #e3e6ea)', padding: '10px 0' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <strong style={{ fontSize: 13 }}>{f.name}</strong>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt(f.createdAt)} · {f.requestedBy || 'client'}{f.staff ? ' · sent from staff view' : ''}</span>
                    <select value={f.status || 'received'} onChange={e => setFeatureStatus(f, e.target.value)} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 6px' }}>
                      {FEATURE_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-line' }}>{f.message}</div>
                </div>
              ))}
            </>
          )}

          {tab === 'requests' && (
            <>
              {requests.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No content requests yet.</div>}
              {requests.map(r => (
                <div key={r.id} style={{ borderTop: '1px solid var(--border-color, #e3e6ea)', padding: '10px 0' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <strong style={{ fontSize: 13 }}>{r.type}</strong>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt(r.createdAt)} · {r.requestedBy || 'client'}{r.neededBy ? ` · needed by ${r.neededBy}` : ''}{r.source === 'chat' ? ' · from chat' : ''}</span>
                    <select value={r.status || 'submitted'} onChange={e => setStatus(r, e.target.value)} style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 6px' }}>
                      {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div style={{ fontSize: 13 }}>{r.about}</div>
                  {r.includeAvoid && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Include/avoid: {r.includeAvoid}</div>}
                </div>
              ))}
            </>
          )}

          {tab === 'chat' && (
            <>
              {messages.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No chat messages yet.</div>}
              {messages.map(m => (
                <div key={m.id} style={{ borderTop: '1px solid var(--border-color, #e3e6ea)', padding: '8px 0', fontSize: 13 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>
                    {m.role === 'user' ? (m.email || 'client') : 'assistant'} · {fmt(m.at)}{m.staff ? ' · staff view' : ''}{m.toolCalls?.length ? ` · ${m.toolCalls.join(', ')}` : ''}{m.capReached ? ' · CAP REACHED' : ''}{m.offTopic ? ' · off-topic' : ''}
                  </div>
                  <div style={{ fontWeight: m.role === 'user' ? 700 : 400 }}>{m.content}</div>
                  {m.displayType && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>[{m.displayType}{m.parsed === false ? ' · plain-text fallback' : ''}]</div>}
                  {m.used?.months?.length ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>used {m.used.months.join(', ')}</div> : null}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
